import React from 'react';
import { TrendingUp, ArrowRight, MapPin, AlertTriangle } from 'lucide-react';
import type { SubmarketScore, ZandiAdjustedProjection } from '../../types/calculations';
import { useActiveMarket } from '../../store/useMarketStore';

interface MarketContextCardProps {
  zandiScoring?: SubmarketScore;
  zandiProjection?: ZandiAdjustedProjection;
  onNavigateToTab2?: () => void;
}

type TrafficLight = 'green' | 'yellow' | 'red';

interface MetricConfig {
  label: string;
  value: string;
  subtext: string;
  signal: TrafficLight;
  signalLabel: string;
}

const getSignalColor = (signal: TrafficLight): { bg: string; text: string; dot: string } => {
  switch (signal) {
    case 'green':
      return { bg: 'bg-emerald-500/10', text: 'text-emerald-400', dot: 'bg-emerald-400' };
    case 'yellow':
      return { bg: 'bg-amber-500/10', text: 'text-amber-400', dot: 'bg-amber-400' };
    case 'red':
      return { bg: 'bg-red-500/10', text: 'text-red-400', dot: 'bg-red-400' };
  }
};

const MetricTile: React.FC<MetricConfig> = ({ label, value, subtext, signal, signalLabel }) => {
  const colors = getSignalColor(signal);

  return (
    <div className="bg-graphite rounded-lg p-4 border border-slate-dark">
      <div className="text-sm text-zinc-500 uppercase tracking-wider mb-1">{label}</div>
      <div className="text-2xl font-mono font-bold text-ivory mb-1">{value}</div>
      <div className="text-sm text-stone-400 mb-2">{subtext}</div>
      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded ${colors.bg}`}>
        <div className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />
        <span className={`text-sm font-medium ${colors.text}`}>{signalLabel}</span>
      </div>
    </div>
  );
};

// SSOT thresholds for traffic light signals based on rent_gap_score (0-100 scale)
const getRentGapSignal = (rentGapScore: number | undefined): { signal: TrafficLight; label: string } => {
  if (rentGapScore === undefined) return { signal: 'yellow', label: 'N/A' };

  // Rent gap score is 0-100, higher = more room for rent growth
  if (rentGapScore >= 60) {
    return { signal: 'green', label: 'HIGH UPSIDE' };
  } else if (rentGapScore >= 40) {
    return { signal: 'yellow', label: 'MODERATE' };
  }
  return { signal: 'red', label: 'LIMITED' };
};

const getCAGRSignal = (cagr: number | undefined): { signal: TrafficLight; label: string } => {
  if (cagr === undefined) return { signal: 'yellow', label: 'N/A' };

  // Backend returns CAGR as decimal ratio (e.g., 0.08 for 8%)
  if (cagr >= 0.08) {
    return { signal: 'green', label: 'STRONG' };
  } else if (cagr >= 0.05) {
    return { signal: 'yellow', label: 'MODERATE' };
  }
  return { signal: 'red', label: 'WEAK' };
};

const getPermitSignal = (score: number | undefined): { signal: TrafficLight; label: string } => {
  if (score === undefined) return { signal: 'yellow', label: 'N/A' };

  if (score >= 70) {
    return { signal: 'green', label: 'HIGH ACTIVITY' };
  } else if (score >= 40) {
    return { signal: 'yellow', label: 'MODERATE' };
  }
  return { signal: 'red', label: 'LOW' };
};

const getCompositeSignal = (score: number | undefined): { signal: TrafficLight; label: string } => {
  if (score === undefined) return { signal: 'yellow', label: 'N/A' };

  if (score >= 70) {
    return { signal: 'green', label: 'STRONG' };
  } else if (score >= 50) {
    return { signal: 'yellow', label: 'MODERATE' };
  }
  return { signal: 'red', label: 'WEAK' };
};

const getSurgeRiskLevel = (precursorScore: number | null | undefined): { level: string; color: TrafficLight } => {
  if (precursorScore == null) return { level: '', color: 'yellow' };
  if (precursorScore >= 60) return { level: 'HIGH', color: 'green' };
  if (precursorScore >= 50) return { level: 'MEDIUM', color: 'yellow' };
  return { level: 'LOW', color: 'red' };
};

export const MarketContextCard: React.FC<MarketContextCardProps> = ({
  zandiScoring,
  zandiProjection,
  onNavigateToTab2,
}) => {
  const market = useActiveMarket();
  if (!zandiScoring) {
    return (
      <div className="bg-charcoal rounded-xl border border-slate-dark p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MapPin className="text-gold" size={20} />
            <h3 className="text-lg font-serif text-ivory">Market Context</h3>
          </div>
        </div>
        <div className="text-center py-6 text-zinc-500">
          <TrendingUp className="mx-auto mb-2 opacity-50" size={24} />
          <p>Zandi market data not available for this property</p>
        </div>
      </div>
    );
  }

  // Extract values from zandi scoring
  const rawData = zandiScoring.raw_data || {};
  // Use rent_gap_score directly (0-100 scale, higher = more room for rent growth)
  const rentGapScore = zandiScoring.rent_gap_score;
  const egiCagr = rawData.egi_cagr_5yr;
  const permitScore = zandiScoring.permit_score;
  const compositeScore = zandiScoring.composite_score;
  const rank = zandiScoring.rank;

  // Get traffic light signals
  const rentGapSignal = getRentGapSignal(rentGapScore);
  const cagrSignal = getCAGRSignal(egiCagr);
  const permitSignal = getPermitSignal(permitScore);
  const compositeSignal = getCompositeSignal(compositeScore);

  // Format values for display
  const formatCAGR = (cagr: number | undefined): string => {
    if (cagr === undefined) return '--';
    // Backend returns CAGR as decimal ratio (e.g., 0.0444 for 4.44%)
    return `${(cagr * 100).toFixed(1)}%`;
  };

  const formatScore = (score: number | undefined): string => {
    if (score === undefined) return '--';
    return score.toFixed(1);
  };

  const metrics: MetricConfig[] = [
    {
      label: 'Submarket Rent Gap',
      value: rentGapScore !== undefined ? formatScore(rentGapScore) : '--',
      subtext: 'vs metro avg (0-100)',
      signal: rentGapSignal.signal,
      signalLabel: rentGapSignal.label,
    },
    {
      label: 'EGI Growth',
      value: formatCAGR(egiCagr),
      subtext: '5yr CAGR',
      signal: cagrSignal.signal,
      signalLabel: cagrSignal.label,
    },
    {
      label: 'Permit Activity',
      value: `${formatScore(permitScore)}/100`,
      subtext: 'Development momentum',
      signal: permitSignal.signal,
      signalLabel: permitSignal.label,
    },
    {
      label: 'Composite Score',
      value: rank ? `${formatScore(compositeScore)} (#${rank})` : formatScore(compositeScore),
      subtext: 'Overall market strength',
      signal: compositeSignal.signal,
      signalLabel: compositeSignal.label,
    },
  ];

  return (
    <div className="bg-charcoal rounded-xl border border-slate-dark p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MapPin className="text-gold" size={20} />
          <h3 className="text-lg font-serif text-ivory">Market Context</h3>
          {zandiScoring.name && (
            <span className="text-sm text-zinc-500 ml-2">{zandiScoring.name}</span>
          )}
        </div>
        {onNavigateToTab2 && (
          <button
            onClick={onNavigateToTab2}
            className="flex items-center gap-1 text-sm text-gold hover:text-gold-bright transition-colors"
          >
            <span>Details</span>
            <ArrowRight size={14} />
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {metrics.map((metric) => (
          <MetricTile key={metric.label} {...metric} />
        ))}
      </div>

      {/* Precursor Score Badge */}
      {zandiProjection?.precursor_score != null && (
        <div className="mt-3 p-3 rounded-lg border border-amber-500/20 bg-amber-500/5">
          <div className="flex items-center gap-2 mb-1.5">
            <AlertTriangle className="text-amber-400" size={14} />
            <span className="text-sm font-semibold text-amber-400 uppercase tracking-wider">
              Surge Precursor Detected
            </span>
            {(() => {
              const risk = getSurgeRiskLevel(zandiProjection.precursor_score);
              const colors = getSignalColor(risk.color);
              return (
                <span className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded ${colors.bg}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />
                  <span className={`text-sm font-medium ${colors.text}`}>{risk.level} RISK</span>
                </span>
              );
            })()}
          </div>
          <p className="text-sm font-mono text-ivory">
            Score: {zandiProjection.precursor_score.toFixed(1)}
          </p>
          {zandiProjection.precursor_match && (
            <p className="text-sm text-stone-400 mt-1">
              Matches: {zandiProjection.precursor_match}
            </p>
          )}
        </div>
      )}

      {/* Submarket Rent Growth Override */}
      {zandiProjection?.submarket_rent_growth && (
        <div className="mt-3 flex items-center gap-3 text-sm text-stone-400">
          <span className="text-gold font-medium">Zandi Growth:</span>
          <span className="font-mono">
            {(zandiProjection.submarket_rent_growth.bear * 100).toFixed(1)}%
            <span className="text-stone-400 mx-1">/</span>
            <span className="text-ivory">{(zandiProjection.submarket_rent_growth.base * 100).toFixed(1)}%</span>
            <span className="text-stone-400 mx-1">/</span>
            {(zandiProjection.submarket_rent_growth.bull * 100).toFixed(1)}%
          </span>
          <span className="text-stone-400">(bear / base / bull)</span>
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-slate-dark">
        <p className="text-sm text-zinc-500">
          <span className="text-gold">Backtested:</span> Composite score has +0.44 correlation with 5-year returns across 174 {market.display_name} submarkets.
        </p>
      </div>
    </div>
  );
};

export default MarketContextCard;
